import { logError } from '../lib/logger';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChevronDown, HelpCircle, Loader2 } from 'lucide-react';
import { parseBoldText } from '../lib/parseBoldText';
import { compareFaqs } from '../lib/contentOrder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CategoryRule({ title }) {
  return (
    <div className="flex items-center gap-3 my-7">
      <div className="flex-1 h-px bg-zinc-900" />
      <h2
        className="text-xl font-semibold text-zinc-900 whitespace-nowrap px-1 uppercase tracking-wide"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {title}
      </h2>
      <div className="flex-1 h-px bg-zinc-900" />
    </div>
  );
}

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openId, setOpenId] = useState(null);

  const grouped = useMemo(() => {
    const sortedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const byCat = sortedCats.map((cat) => ({
      ...cat,
      faqs: faqs
        .filter((f) => f.category_id === cat.id)
        .sort(compareFaqs),
    }));
    const knownIds = new Set(sortedCats.map((c) => c.id));
    const orphanFaqs = faqs
      .filter((f) => !f.category_id || !knownIds.has(f.category_id))
      .sort(compareFaqs);
    if (orphanFaqs.length > 0) {
      byCat.push({
        id: '_orphan',
        title: 'Altro',
        description: '',
        faqs: orphanFaqs,
      });
    }
    return byCat.filter((g) => g.faqs.length > 0);
  }, [categories, faqs]);

  const loadData = async () => {
    setLoadError('');
    setLoading(true);
    try {
      const [faqsRes, catsRes] = await Promise.all([
        axios.get(`${API}/faqs`),
        axios.get(`${API}/faq-categories`),
      ]);
      setFaqs(faqsRes.data);
      setCategories(catsRes.data);
    } catch (err) {
      logError('FAQ', err);
      setLoadError('Impossibile caricare le FAQ. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#D4AF37]/10 mb-4">
            <HelpCircle className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="text-3xl font-light text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Domande <span className="text-gold-gradient font-medium">Frequenti</span>
          </h1>
          <p className="text-zinc-600">Risposte organizzate per argomento</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>
        ) : loadError ? (
          <div className="text-center py-12" role="alert">
            <p className="text-red-700 text-sm mb-4">{loadError}</p>
            <button
              type="button"
              onClick={loadData}
              className="px-4 py-2 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] text-white text-sm font-medium"
            >
              Riprova
            </button>
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-12" data-testid="faq-empty">
            <p className="text-zinc-500">Nessuna FAQ disponibile. Torna più tardi.</p>
          </div>
        ) : (
          <div>
            {grouped.map((section) => (
              <section key={section.id} data-testid={`faq-section-${section.id}`}>
                <CategoryRule title={section.title} />
                {section.description ? (
                  <p className="text-center text-sm text-zinc-500 mb-4 -mt-3 leading-relaxed">
                    {section.description}
                  </p>
                ) : null}

                {section.faqs.map((faq, idx) => {
                  const isOpen = openId === faq.id;
                  return (
                    <div key={faq.id} data-testid={`faq-item-${faq.id}`}>
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : faq.id)}
                        className="w-full flex items-center justify-between gap-4 py-4 text-left font-normal group"
                        data-testid={`faq-toggle-${faq.id}`}
                      >
                        <span className="text-zinc-900 text-[15px] leading-snug pr-2 font-normal">
                          {parseBoldText(faq.question)}
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 flex-shrink-0 text-[#D4AF37] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {isOpen && (
                        <div className="pb-4 animate-fade-in">
                          <div className="text-zinc-700 text-[15px] leading-relaxed whitespace-pre-wrap font-normal">
                            {parseBoldText(faq.answer)}
                          </div>
                        </div>
                      )}

                      {idx < section.faqs.length - 1 && (
                        <div className="h-px bg-[#D4AF37]/20" />
                      )}
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
        )}
    </main>
  );
};

export default FAQ;
